import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type {
  TimelineState,
  LifeEvent,
  LifePhase,
  LifeEventCategory,
  FinancialImpact,
  LifeEventType,
  PhaseBudget,
} from '@/types'
import { createInitialTimelineState, PHASE_COLORS, EVENT_CATEGORY_MAP } from '@/types/timeline.types'

const initialState: TimelineState = createInitialTimelineState()

// Helper to generate unique IDs
function generateId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Helper to check if event is in the past
function isEventPast(date: string): boolean {
  return new Date(date) < new Date()
}

const timelineSlice = createSlice({
  name: 'timeline',
  initialState,
  reducers: {
    // ========================================
    // LIFE EVENTS
    // ========================================
    addEvent: (state, action: PayloadAction<Omit<LifeEvent, 'id' | 'isPast' | 'category'> & { category?: LifeEventCategory }>) => {
      const { type, category, ...rest } = action.payload
      const newEvent: LifeEvent = {
        ...rest,
        id: generateId('event'),
        type,
        category: category || EVENT_CATEGORY_MAP[type],
        isPast: isEventPast(rest.date),
      }
      state.events.push(newEvent)
      // Sort events by date
      state.events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    },
    
    updateEvent: (state, action: PayloadAction<{ id: string; updates: Partial<LifeEvent> }>) => {
      const event = state.events.find(e => e.id === action.payload.id)
      if (event) {
        Object.assign(event, action.payload.updates)
        if (action.payload.updates.date) {
          event.isPast = isEventPast(action.payload.updates.date)
        }
        // Re-sort if date changed
        state.events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      }
    },
    
    removeEvent: (state, action: PayloadAction<string>) => {
      state.events = state.events.filter(e => e.id !== action.payload)
    },
    
    updateEventFinancialImpact: (state, action: PayloadAction<{ id: string; impact: FinancialImpact }>) => {
      const event = state.events.find(e => e.id === action.payload.id)
      if (event) {
        event.financialImpact = action.payload.impact
      }
    },
    
    moveEvent: (state, action: PayloadAction<{ id: string; newDate: string }>) => {
      const event = state.events.find(e => e.id === action.payload.id)
      if (event) {
        event.date = action.payload.newDate
        event.isPast = isEventPast(action.payload.newDate)
        state.events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      }
    },
    
    // Bulk add events (for auto-generation from life plan)
    setEvents: (state, action: PayloadAction<LifeEvent[]>) => {
      state.events = action.payload.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    },
    
    // ========================================
    // LIFE PHASES
    // ========================================
    addPhase: (state, action: PayloadAction<Omit<LifePhase, 'id' | 'isActive'>>) => {
      const now = new Date()
      const start = new Date(action.payload.startDate)
      const end = new Date(action.payload.endDate)
      
      const newPhase: LifePhase = {
        ...action.payload,
        id: generateId('phase'),
        isActive: now >= start && now <= end,
        color: action.payload.color || PHASE_COLORS[action.payload.type],
      }
      state.phases.push(newPhase)
      // Sort phases by start date
      state.phases.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
    },
    
    updatePhase: (state, action: PayloadAction<{ id: string; updates: Partial<LifePhase> }>) => {
      const phase = state.phases.find(p => p.id === action.payload.id)
      if (phase) {
        Object.assign(phase, action.payload.updates)
        // Recalculate isActive
        const now = new Date()
        const start = new Date(phase.startDate)
        const end = new Date(phase.endDate)
        phase.isActive = now >= start && now <= end
        // Re-sort if dates changed
        state.phases.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
      }
    },
    
    removePhase: (state, action: PayloadAction<string>) => {
      state.phases = state.phases.filter(p => p.id !== action.payload)
    },
    
    updatePhaseBudget: (state, action: PayloadAction<{ id: string; budget: Partial<PhaseBudget> }>) => {
      const phase = state.phases.find(p => p.id === action.payload.id)
      if (phase) {
        phase.budget = { ...phase.budget, ...action.payload.budget }
      }
    },
    
    // Bulk set phases
    setPhases: (state, action: PayloadAction<LifePhase[]>) => {
      state.phases = action.payload.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
    },
    
    // ========================================
    // VIEW SETTINGS
    // ========================================
    setViewRange: (state, action: PayloadAction<{ startYear: number; endYear: number }>) => {
      state.viewStartYear = action.payload.startYear
      state.viewEndYear = action.payload.endYear
    },
    
    setZoomLevel: (state, action: PayloadAction<TimelineState['zoomLevel']>) => {
      state.zoomLevel = action.payload
    },
    
    selectEvent: (state, action: PayloadAction<string | null>) => {
      state.selectedEventId = action.payload
      state.selectedPhaseId = null
    },
    
    selectPhase: (state, action: PayloadAction<string | null>) => {
      state.selectedPhaseId = action.payload
      state.selectedEventId = null
    },
    
    clearSelection: (state) => {
      state.selectedEventId = null
      state.selectedPhaseId = null
    },
    
    // ========================================
    // FILTERS
    // ========================================
    toggleShowPastEvents: (state) => {
      state.showPastEvents = !state.showPastEvents
    },
    
    setVisibleCategories: (state, action: PayloadAction<LifeEventCategory[]>) => {
      state.visibleCategories = action.payload
    },
    
    toggleCategory: (state, action: PayloadAction<LifeEventCategory>) => {
      const index = state.visibleCategories.indexOf(action.payload)
      if (index >= 0) {
        state.visibleCategories.splice(index, 1)
      } else {
        state.visibleCategories.push(action.payload)
      }
    },
    
    // ========================================
    // AUTO-GENERATION FROM LIFE PLAN
    // ========================================
    generateEventsFromLifePlan: (state, action: PayloadAction<{
      personal: { birthDate: string; retirementAge: number; lifeExpectancy: number }
      family: { 
        children: Array<{ id: string; firstName: string; birthDate: string; collegeFundingPlan: string }>
        plannedChildren: Array<{ id: string; plannedYear: number }>
      }
      career: { plannedChanges: Array<{ id: string; type: string; targetDate: string; salaryChange: number; description: string }> }
      preferences: { socialSecurityAge: number; hasPension: boolean; pensionStartAge: number }
      goals: Array<{ id: string; name: string; targetDate: string; targetAmount: number }>
    }>) => {
      const { personal, family, career, preferences, goals } = action.payload
      const events: LifeEvent[] = []
      
      // Calculate birth year for age calculations
      const birthYear = personal.birthDate ? new Date(personal.birthDate).getFullYear() : new Date().getFullYear() - 30
      
      // Add retirement event
      if (personal.retirementAge) {
        const retirementYear = birthYear + personal.retirementAge
        events.push({
          id: generateId('event'),
          type: 'retirement_start',
          category: 'retirement',
          name: 'Retirement',
          date: `${retirementYear}-01-01`,
          financialImpact: { incomeChange: 0 },
          isAutoGenerated: true,
          isPast: false,
          confidence: 'likely',
          isRecurring: false,
        })
      }
      
      // Add Social Security event
      if (preferences.socialSecurityAge) {
        const ssYear = birthYear + preferences.socialSecurityAge
        events.push({
          id: generateId('event'),
          type: 'social_security_start',
          category: 'retirement',
          name: 'Social Security Starts',
          date: `${ssYear}-01-01`,
          financialImpact: {},
          isAutoGenerated: true,
          isPast: false,
          confidence: 'likely',
          isRecurring: false,
        })
      }
      
      // Add pension event
      if (preferences.hasPension && preferences.pensionStartAge) {
        const pensionYear = birthYear + preferences.pensionStartAge
        events.push({
          id: generateId('event'),
          type: 'pension_start',
          category: 'retirement',
          name: 'Pension Starts',
          date: `${pensionYear}-01-01`,
          financialImpact: {},
          isAutoGenerated: true,
          isPast: false,
          confidence: 'likely',
          isRecurring: false,
        })
      }
      
      // Add children college events
      family.children.forEach(child => {
        if (child.collegeFundingPlan !== 'none') {
          const childBirthYear = new Date(child.birthDate).getFullYear()
          const collegeStartYear = childBirthYear + 18
          events.push({
            id: generateId('event'),
            type: 'child_college_start',
            category: 'family',
            name: `${child.firstName} Starts College`,
            date: `${collegeStartYear}-09-01`,
            financialImpact: {},
            linkedChildId: child.id,
            isAutoGenerated: true,
            isPast: false,
            confidence: 'likely',
            isRecurring: false,
          })
        }
      })
      
      // Add planned children events
      family.plannedChildren.forEach(planned => {
        events.push({
          id: generateId('event'),
          type: 'child_born',
          category: 'family',
          name: 'Planned Child Born',
          date: `${planned.plannedYear}-06-01`,
          financialImpact: { expenseChange: 15000 },  // Rough estimate
          linkedChildId: planned.id,
          isAutoGenerated: true,
          isPast: false,
          confidence: 'possible',
          isRecurring: false,
        })
      })
      
      // Add career change events
      career.plannedChanges.forEach(change => {
        events.push({
          id: generateId('event'),
          type: change.type as LifeEventType,
          category: 'career',
          name: change.description || change.type,
          date: change.targetDate,
          financialImpact: { incomeChange: change.salaryChange },
          isAutoGenerated: true,
          isPast: isEventPast(change.targetDate),
          confidence: 'likely',
          isRecurring: false,
        })
      })
      
      // Add goal target events
      goals.forEach(goal => {
        events.push({
          id: generateId('event'),
          type: 'goal_achieved',
          category: 'financial',
          name: goal.name,
          date: goal.targetDate,
          financialImpact: { oneTimeExpense: goal.targetAmount },
          linkedGoalId: goal.id,
          isAutoGenerated: true,
          isPast: isEventPast(goal.targetDate),
          confidence: 'likely',
          isRecurring: false,
        })
      })
      
      // Merge with existing manual events
      const manualEvents = state.events.filter(e => !e.isAutoGenerated)
      state.events = [...manualEvents, ...events].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      )
    },
    
    generatePhasesFromLifePlan: (state, action: PayloadAction<{
      birthDate: string
      retirementAge: number
      lifeExpectancy: number
      currentIncome: number
    }>) => {
      const { birthDate, retirementAge, lifeExpectancy, currentIncome } = action.payload
      const birthYear = birthDate ? new Date(birthDate).getFullYear() : new Date().getFullYear() - 30
      const currentYear = new Date().getFullYear()
      const currentAge = currentYear - birthYear
      
      const phases: LifePhase[] = []
      
      // Create default budget template
      const createBudget = (income: number): PhaseBudget => ({
        expectedIncome: income,
        incomeGrowthRate: 3,
        housing: income * 0.25,
        utilities: 200 * 12,
        insurance: 300 * 12,
        transportation: 500 * 12,
        food: 600 * 12,
        healthcare: 200 * 12,
        childcare: 0,
        education: 0,
        entertainment: 200 * 12,
        travel: 300 * 12,
        clothing: 100 * 12,
        personalCare: 100 * 12,
        retirement401k: income * 0.1,
        retirementIRA: 0,
        taxableSavings: income * 0.05,
        emergencyFund: 0,
        debtPayments: 0,
        charitable: 0,
        other: 200 * 12,
      })
      
      // Early career (now to 35 or current age if older)
      if (currentAge < 35) {
        phases.push({
          id: generateId('phase'),
          type: 'early_career',
          name: 'Early Career',
          description: 'Building career foundation and paying off student debt',
          startDate: `${currentYear}-01-01`,
          endDate: `${birthYear + 35}-12-31`,
          budget: createBudget(currentIncome),
          color: PHASE_COLORS.early_career,
          isActive: true,
          isAutoGenerated: true,
        })
      }
      
      // Career growth (35-50)
      if (currentAge < 50) {
        const startYear = Math.max(currentYear, birthYear + 35)
        phases.push({
          id: generateId('phase'),
          type: 'career_growth',
          name: 'Career Growth',
          description: 'Peak earning years and family expenses',
          startDate: `${startYear}-01-01`,
          endDate: `${birthYear + 50}-12-31`,
          budget: createBudget(currentIncome * 1.5),
          color: PHASE_COLORS.career_growth,
          isActive: currentAge >= 35 && currentAge < 50,
          isAutoGenerated: true,
        })
      }
      
      // Peak earning (50 to retirement)
      if (currentAge < retirementAge) {
        const startYear = Math.max(currentYear, birthYear + 50)
        phases.push({
          id: generateId('phase'),
          type: 'peak_earning',
          name: 'Peak Earning',
          description: 'Maximizing retirement savings',
          startDate: `${startYear}-01-01`,
          endDate: `${birthYear + retirementAge - 1}-12-31`,
          budget: createBudget(currentIncome * 1.8),
          color: PHASE_COLORS.peak_earning,
          isActive: currentAge >= 50 && currentAge < retirementAge,
          isAutoGenerated: true,
        })
      }
      
      // Early retirement (retirement to 75)
      phases.push({
        id: generateId('phase'),
        type: 'early_retirement',
        name: 'Early Retirement',
        description: 'Active retirement years',
        startDate: `${birthYear + retirementAge}-01-01`,
        endDate: `${birthYear + 75}-12-31`,
        budget: createBudget(currentIncome * 0.7),
        color: PHASE_COLORS.early_retirement,
        isActive: currentAge >= retirementAge && currentAge < 75,
        isAutoGenerated: true,
      })
      
      // Full retirement (75 to life expectancy)
      phases.push({
        id: generateId('phase'),
        type: 'full_retirement',
        name: 'Full Retirement',
        description: 'Later retirement years',
        startDate: `${birthYear + 75}-01-01`,
        endDate: `${birthYear + lifeExpectancy}-12-31`,
        budget: createBudget(currentIncome * 0.5),
        color: PHASE_COLORS.full_retirement,
        isActive: currentAge >= 75,
        isAutoGenerated: true,
      })
      
      // Merge with existing manual phases
      const manualPhases = state.phases.filter(p => !p.isAutoGenerated)
      state.phases = [...manualPhases, ...phases].sort(
        (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      )
    },
    
    // ========================================
    // FULL STATE
    // ========================================
    setTimelineData: (state, action: PayloadAction<Partial<TimelineState>>) => {
      Object.assign(state, action.payload)
    },
    
    resetTimeline: () => createInitialTimelineState(),
  },
})

export const {
  addEvent,
  updateEvent,
  removeEvent,
  updateEventFinancialImpact,
  moveEvent,
  setEvents,
  addPhase,
  updatePhase,
  removePhase,
  updatePhaseBudget,
  setPhases,
  setViewRange,
  setZoomLevel,
  selectEvent,
  selectPhase,
  clearSelection,
  toggleShowPastEvents,
  setVisibleCategories,
  toggleCategory,
  generateEventsFromLifePlan,
  generatePhasesFromLifePlan,
  setTimelineData,
  resetTimeline,
} = timelineSlice.actions

export default timelineSlice.reducer
